import { useState, SetStateAction, Dispatch, useContext } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { FormControl, Autocomplete, TextField, Box, FormControlLabel, Checkbox } from '@mui/material';

import CustomDialog from '../../../common/CustomDialog';
import { useAppDispatch } from '../../../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { UnitContext } from '../../../../context/UnitContext';
import { selectProject } from '../../../../features/project/projectSlice';
import { selectUser } from '../../../../features/auth/authSlice';
import { useCreateActivity } from '../../../../features/activity/activityHooks';
import { IRealtorInfo, IDealRealtor, IPurchaserInfo } from '../../../../types/CreateDealForm';
import { IDocuments } from '../../../../types/docusign';
import LoadingWrapper from '../../../common/LoadingWrapper';
import { IMerge } from '../../../../types/merge';
import { createPdf, getMergeValues } from '../../../../utils/Functions';

const RealtorDialog = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const createActivity = useCreateActivity();
  const { unit, filteredDeal, setFilteredDeal, realtors, realtorLoading, updateDealDocuments, assignment } = useContext(UnitContext);
  const { dialogType, setDialogType, dialogOpen, setDialogOpen, realtorIndex, merges } = props;
  const [selectedRealtor, setSelectedRealtor] = useState<IRealtorInfo>({});
  const [selectedMerge, setSelectedMerge] = useState<IMerge | null>(null);
  const [selectedPurchasers, setSelectedPurchasers] = useState<IPurchaserInfo[]>([]);

  const [createDocument, { loading: createDocumentLoading }] = useMutation(CREATEDOCUMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`Document has been created!`));
      let documentIds = filteredDeal.documents.map((document: IDocuments) => {
        return document._id;
      });
      updateDealDocuments({
        variables: { _id: filteredDeal._id, record: { documents: [...documentIds, data.documentCreateOne._id] } },
      });
      setFilteredDeal({
        ...filteredDeal,
        documents: [...filteredDeal.documents, data.documentCreateOne],
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [addRealtor] = useMutation(ADDREALTOR, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Realtor has been added to the deal'));
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Add Realtor`,
        content: `Realtor has been added to the deal`,
      });
      let dealRealtorIds = filteredDeal.realtor.map((realtor: IDealRealtor) => realtor._id);
      updateDealRealtor({
        variables: { _id: filteredDeal._id, record: { realtor: [...dealRealtorIds, data.dealRealtorCreateOne.record._id] } },
      });
      setFilteredDeal({
        ...filteredDeal,
        realtor: [
          ...filteredDeal.realtor,
          {
            _id: data.dealRealtorCreateOne.record._id,
            firstName: data.dealRealtorCreateOne.record.firstName,
            lastName: data.dealRealtorCreateOne.record.lastName,
            fullName: `${data.dealRealtorCreateOne.record.firstName} ${data.dealRealtorCreateOne.record.lastName}`,
            brokerage: data.dealRealtorCreateOne.record.brokerage,
            email: data.dealRealtorCreateOne.record.email,
            streetAddress: data.dealRealtorCreateOne.record.streetAddress,
            city: data.dealRealtorCreateOne.record.city,
            province: data.dealRealtorCreateOne.record.province,
            country: data.dealRealtorCreateOne.record.country,
            postalCode: data.dealRealtorCreateOne.record.postalCode,
            brokeragePhone: data.dealRealtorCreateOne.record.brokeragePhone,
            directPhone: data.dealRealtorCreateOne.record.directPhone,
            brokerageFax: data.dealRealtorCreateOne.record.brokerageFax,
            coopRates: data.dealRealtorCreateOne.record.coopRates,
          },
        ],
      });
      if (filteredDeal.realtor.length > 0) {
        updateAllocation({
          variables: {
            _id: unit._id,
            record: {
              allocation: selectedRealtor._id,
            },
          },
        });
      }
      setSelectedRealtor({});
      setDialogType('');
      setDialogOpen(false);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateDealRealtor] = useMutation(DELETEREALTORDEAL, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateAllocation] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deleteRealtor] = useMutation(DELETEREALTOR, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Realtor has been removed from the deal'));
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Remove Realtor`,
        content: `Realtor has been removed from the deal`,
      });
      let dealRealtorIds = filteredDeal.realtor.filter((realtor: IDealRealtor) => realtor._id !== data.dealRealtorRemoveById.record._id);
      updateDealRealtor({
        variables: { _id: filteredDeal._id, record: { realtor: dealRealtorIds.map((realtor: IDealRealtor) => realtor._id) } },
      });
      updateAllocation({
        variables: {
          _id: unit._id,
          record: {
            allocation: null,
          },
        },
      });
      setFilteredDeal({
        ...filteredDeal,
        realtor: dealRealtorIds,
      });
      setDialogType('');
      setDialogOpen(false);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const generateMultiplePdfs = async () => {
    const mergedPdf = await PDFDocument.create();

    let documents = [];

    if (selectedPurchasers.length) {
      let mandatary = merges.find((merge: IMerge) => merge.type === 'Mandatary');
      if (!selectedMerge && !mandatary) return storeDispatch(showErrorSnackbar('No Coop Selected'));
      for (const [index, purchaser] of selectedPurchasers.entries()) {
        let fintracMerges = [mandatary!];
        if (purchaser.corp) {
          let fintrac = merges.find((merge: IMerge) => merge.name === 'Corp Fintrac - Realtor');
          fintracMerges.push(fintrac!);
        } else {
          let fintrac = merges.find((merge: IMerge) => merge.name === 'Fintrac - Realtor');
          fintracMerges.push(fintrac!);
        }

        for (const document of fintracMerges) {
          let autoMergeTemplate = merges.find((mergeTemplate: IMerge) => mergeTemplate.name === document.name);

          let autoDocument = await fetch(autoMergeTemplate?.getUrl!).then(async (res) => await res.arrayBuffer());
          const documentPdf = await PDFDocument.load(autoDocument);
          let helveticaFont = await documentPdf.embedFont(StandardFonts.Helvetica);

          let pages = documentPdf.getPages();
          await autoMergeTemplate?.mergeFields.forEach(async (mergeField: any) => {
            let mergePages = pages[mergeField.pageNumber];
            let mergeValue = getMergeValues(
              mergeField,
              project,
              unit,
              null,
              [purchaser],
              [],
              [],
              filteredDeal.deposit,
              filteredDeal.options,
              filteredDeal.realtor[realtorIndex],
              null,
              null,
              null,
              null,
              filteredDeal
            );

            mergePages.drawText(mergeValue, {
              x: mergeField.x,
              y: mergePages.getHeight() - mergeField.y - 3,
              size: mergeField.fontSize,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            });
          });

          let copiedPages = await mergedPdf.copyPages(documentPdf, documentPdf.getPageIndices());
          copiedPages.forEach((page: any) => mergedPdf.addPage(page));

          const documentDoc: any = await documentPdf.save();

          let mergeSignFields = autoMergeTemplate?.signFields.map((signFields: any, index: number) => {
            return {
              index: signFields.index,
              key: signFields.key,
              pageNumber: signFields.pageNumber,
              x: Math.round(signFields.x),
              y: Math.round(signFields.y),
              title: signFields.title,
            };
          });

          let blob = await new Blob([documentDoc]);
          let file = await new File([blob], `${document.name}.pdf`, { type: 'application/pdf' });

          documents.push({
            file: file,
            name: `${document.name.replace('- Realtor', '')} For P${index + 1}`,
            mergeTemplate: document._id,
            signFields: mergeSignFields,
            isAPS: false,
            type: selectedMerge ? 'Coop' : 'Fintrac',
          });
        }
      }
    }

    let name = documents.map((document: any) => document.name).join(' + ');

    const pdfBytes: any = await mergedPdf.save();

    const blob = new Blob([pdfBytes]);
    const file = new File([blob], `${name}.pdf`, { type: 'application/pdf' });

    const args = {
      status: 'Prepared',
      name: name,
      project: project._id,
      deal: filteredDeal._id,
      // signers,
      isAPS: false,
      type: selectedMerge ? 'Coop' : 'Fintrac',
      mergeTemplate: documents.map((document: any) => document.mergeTemplate),
    };

    createDocument({ variables: { record: args, file: file } });

    setDialogOpen(false);
  };

  const handleCloseSuccess = async () => {
    if (dialogType === 'addRealtor') {
      const { _id, ...newObject } = selectedRealtor;
      newObject.deal = filteredDeal._id;
      newObject.project = project._id;
      newObject.realtor = _id;
      addRealtor({ variables: { record: newObject } });
    } else if (dialogType === 'deleteRealtor') {
      deleteRealtor({ variables: { _id: filteredDeal.realtor[realtorIndex]._id } });
    } else if (dialogType === 'amendment') {
      if (selectedMerge && !selectedPurchasers.length) {
        let pdf = await createPdf(
          selectedMerge,
          project,
          unit,
          filteredDeal.purchasers,
          [],
          [],
          filteredDeal.deposit,
          filteredDeal.options,
          filteredDeal.realtor[realtorIndex],
          null,
          null,
          null,
          filteredDeal
        );

        let sign = selectedMerge.signFields.map((sign: any) => {
          return {
            index: sign.index,
            key: sign.key,
            pageNumber: sign.pageNumber,
            x: sign.x,
            y: sign.y,
            title: sign.title,
          };
        });

        let mergeName = `${selectedMerge.name} - ${filteredDeal.realtor[realtorIndex].firstName} ${filteredDeal.realtor[realtorIndex].lastName}`;

        const args = {
          status: 'Prepared',
          name: mergeName,
          project: project._id,
          deal: filteredDeal._id,
          signFields: sign,
          // signers,
          isAPS: false,
          type: selectedMerge.type,
          mergeTemplate: selectedMerge ? selectedMerge._id : null,
        };
        setDialogOpen(false);
        createDocument({ variables: { record: args, file: pdf } });
      } else if (selectedPurchasers.length) {
        generateMultiplePdfs();
      }
    }
  };

  const dialogContent = () => {
    if (dialogType === 'addRealtor') {
      return realtorLoading ? (
        <LoadingWrapper title="Realtors is loading..." modal={false} />
      ) : (
        <div>
          <FormControl
            sx={{
              width: '100%',
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              },
            }}
          >
            <Autocomplete
              sx={{
                width: '100%',
                '& .MuiFormLabel-asterisk': {
                  color: 'red',
                },
              }}
              id={'search'}
              disableClearable
              freeSolo={false}
              options={realtors}
              getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} ${option.email}`}
              onChange={(e, values) => setSelectedRealtor(values)}
              renderInput={(params) => (
                <TextField required={false} {...params} size="small" label={'Search By Name or Email'} margin="normal" />
              )}
            />
          </FormControl>
        </div>
      );
    } else if (dialogType === 'amendment') {
      return (
        <Box sx={{ mt: 1 }}>
          {!selectedPurchasers.length ? 
            <Autocomplete
              options={merges}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              disableClearable={false}
              freeSolo={false}
              value={selectedMerge}
              onChange={(event: any, newValue: any | null) => {
                setSelectedPurchasers([]);
                setSelectedMerge(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  sx={{ width: '500px', '@media (max-width:1499.98px)': { width: '400px' } }}
                  {...params}
                  label="Coop Merge Templates"
                  size="medium"
                />
              )}
            />
          : null}
          {!selectedMerge ? (
            <Box>
              <Box sx={{ mt: 2 }}>
                <strong>Select the purchasers to be included for fintrac and mandatary forms.</strong>
              </Box>
              {filteredDeal.purchasers.map((purchaser: IPurchaserInfo) => {
                return (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="purchaser"
                          value={purchaser._id}
                          color="primary"
                          checked={selectedPurchasers.some((selectedPurchaser: any) => selectedPurchaser._id === purchaser._id)}
                          onChange={(e: any) => {
                            if (selectedPurchasers.some((selectedPurchaser: any) => purchaser._id === selectedPurchaser._id)) {
                              setSelectedPurchasers(selectedPurchasers.filter((selected: any) => selected._id !== purchaser._id));
                            } else {
                              setSelectedPurchasers([...selectedPurchasers, purchaser]);
                            }
                          }}
                        />
                      }
                      label={`${purchaser.firstName} ${purchaser.lastName}`}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      );
    }
  };

  const handleDialogButton = () => {
    if (dialogType === 'addRealtor') {
      return 'Yes, I would like to add this realtor';
    } else if (dialogType === 'deleteRealtor') {
      return 'Yes, delete this realtor';
    } else if (dialogType === 'amendment') {
      return 'Create Amendment';
    }
  };

  const handleDialogTitle = () => {
    if (dialogType === 'addRealtor') {
      return 'Please select a Realtor';
    } else if (dialogType === 'deleteRealtor') {
      return 'Are you sure you would like to remove this realtor?';
    } else if (dialogType === 'amendment') {
      return 'Please select an amendment';
    }
  };

  return (
    <CustomDialog
      handleClose={() => {
        setSelectedMerge(null);
        setDialogOpen(false);
        setDialogType('');
      }}
      handleCloseRemove={() => {
        setSelectedMerge(null);
        setDialogOpen(false);
        setDialogType('');
      }}
      handleCloseSuccess={handleCloseSuccess}
      open={dialogOpen}
      removeButton={'Cancel'}
      loading={createDocumentLoading}
      loadingText={'Creating Document...'}
      successButton={handleDialogButton()!}
      dialogContent={dialogContent()}
      dialogTitle={handleDialogTitle()!}
    />
  );
};

interface ChildProps {
  dialogOpen: boolean;
  dialogType: string;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  setDialogType: Dispatch<SetStateAction<string>>;
  realtorIndex: number;
  merges: IMerge[];
}

const CREATEDOCUMENT = gql`
  mutation documentCreateOne($record: CreateOneDocumentInput, $file: Upload) {
    documentCreateOne(record: $record, file: $file) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
      mergeTemplate
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        suite
        unit
        level
        modelType
        basePrice
        size
        status
        getUrl
        putUrl
        bathroom
        exposure
        status
        outdoor {
          name
          size
        }
        unitType
        originalPrice
        basePrice
        allocation {
          _id
          fullName
        }
        history {
          type
          description
          timestamp
          user {
            _id
            fullName
          }
          _id
        }
      }
    }
  }
`;

const ADDREALTOR = gql`
  mutation dealRealtorCreateOne($record: CreateOneDealRealtorInput!) {
    dealRealtorCreateOne(record: $record) {
      record {
        _id
        firstName
        lastName
        brokerage
        email
        streetAddress
        city
        province
        country
        postalCode
        brokeragePhone
        directPhone
        brokerageFax
        coopRates {
          type
          days
          date
          description
          percentage
          fixedAmount
        }
      }
    }
  }
`;

const DELETEREALTORDEAL = gql`
  mutation dealUpdateById($_id: MongoID!, $record: UpdateByIdDealInput!) {
    dealUpdateById(_id: $_id, record: $record) {
      record {
        realtor {
          _id
        }
      }
    }
  }
`;

const DELETEREALTOR = gql`
  mutation dealRealtorRemoveById($_id: MongoID!) {
    dealRealtorRemoveById(_id: $_id) {
      record {
        _id
      }
    }
  }
`;

export default RealtorDialog;
